import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { studyAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';

// Debounce a value so search doesn't fire on every keystroke.
const useDebounce = (value, delay) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debounced;
};

const StudyList = () => {
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';
  const canEdit = ['admin', 'lab_manager', 'lab_technician'].includes(currentUser?.role);

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const fetchStudies = useCallback(async (search = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (search?.trim()) params.append('search', search.trim());
      const response = await studyAPI.getAll(`?${params.toString()}`);
      setStudies(response.data.studies || []);
      setError('');
    } catch (err) {
      console.error('Error fetching studies', err);
      setError('Failed to load studies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStudies(debouncedSearchTerm);
  }, [debouncedSearchTerm, fetchStudies]);

  const handleDelete = async (e, study) => {
    e.stopPropagation();
    if (
      !window.confirm(
        `Delete study "${study.name}"? Its ${study.project_count} project(s) will be unassigned from the study, not deleted.`
      )
    ) {
      return;
    }
    try {
      await studyAPI.delete(study.id);
      toast.success('Study deleted; projects detached');
      fetchStudies(debouncedSearchTerm);
    } catch (err) {
      console.error('Error deleting study', err);
      toast.error('Failed to delete study');
    }
  };

  return (
    <Box className="study-list page-container">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Studies
        </Typography>
        {canEdit && (
          <Button
            component={Link}
            to="/studies/new"
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
          >
            New Study
          </Button>
        )}
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        A study groups the projects that belong to one investigation (a cohort can span several
        projects). Each project belongs to at most one study.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          label="Search Studies"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by study number, name, or description"
          InputProps={{ endAdornment: <SearchIcon color="action" /> }}
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Study</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right">Projects</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <CircularProgress size={24} sx={{ my: 2 }} />
                </TableCell>
              </TableRow>
            ) : studies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  {searchTerm ? 'No studies match your search' : 'No studies yet'}
                </TableCell>
              </TableRow>
            ) : (
              studies.map((study) => (
                <TableRow
                  key={study.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/studies/${study.id}`)}
                >
                  <TableCell>
                    <Typography variant="body2">{study.name}</Typography>
                    {study.study_number != null && (
                      <Typography variant="caption" color="text.secondary">
                        #{study.study_number}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 360 }}>{study.description || '—'}</TableCell>
                  <TableCell align="right">
                    <Chip size="small" label={study.project_count} />
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {canEdit && (
                      <>
                        <IconButton
                          size="small"
                          component={Link}
                          to={`/studies/edit/${study.id}`}
                          title="Edit"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        {isAdmin && (
                          <IconButton
                            size="small"
                            onClick={(e) => handleDelete(e, study)}
                            title="Delete"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default StudyList;
